// useShareThread — copy a thread into a new thread owned by another user
// (POST /sessions/{id}/share). Same one-off createApiClient() pattern as
// useDeleteThread/useUpdateTitle. No ongoing access is granted — the
// recipient gets a fully independent, owned copy — so there is nothing to
// list or revoke here, just invalidate their `shared-with-me` view.

import { useMutation } from '@tanstack/react-query';
import { createApiClient } from '@/shared/api';
import { getToken } from '@/shared/auth';
import { readEnv } from '@/shared/config';

function getClient() {
  return createApiClient({
    baseUrl: readEnv('VITE_API_BASE_URL') ?? '',
    auth: { getToken },
  });
}

async function shareThread(threadId: string, userId: string): Promise<string> {
  const raw = await getClient().request<{ session_id?: unknown }>({
    method: 'POST',
    path: `/api/v1/sessions/${encodeURIComponent(threadId)}/share`,
    body: { user_id: userId },
  });
  return typeof raw.session_id === 'string' ? raw.session_id : '';
}

export function useShareThread() {
  // No local invalidation on success — sharing creates a new thread for the
  // *recipient*, not the caller; the caller's own thread list/detail is
  // unaffected.
  const mutation = useMutation({
    mutationFn: ({ threadId, userId }: { threadId: string; userId: string }) => shareThread(threadId, userId),
  });
  return {
    shareThread: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
